import clsx from "clsx";
import { Link } from "react-router-dom";

import {
  ITicketInsightAgeBucket,
  ITicketInsightCustomer,
  ITicketInsightKnowledgeGap,
  ITicketInsightTag,
} from "../../../models/Tickets/TicketInsight";
import { BarRow, Card, EmptyState, StackedBar } from "./InsightWidgets";
import {
  categoryBarClassMap,
  categoryByName,
  categoryLabels,
  CategoryPill,
} from "./insightMeta";

export function CustomersCard({
  customers,
  onSelectCustomer,
}: {
  customers: ITicketInsightCustomer[];
  onSelectCustomer: (customerId: string) => void;
}) {
  return (
    <Card
      title="Customers raising the most"
      description="Click a customer to scope the whole page to them."
    >
      {customers.length === 0 ? (
        <EmptyState>No analysed tickets in this scope yet.</EmptyState>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold text-gray-500">
                <th className="py-1 pr-3">Customer</th>
                <th className="py-1 pr-3 text-right">Tickets</th>
                <th className="py-1 pr-3 text-right">Active</th>
                <th className="py-1 pr-3 text-right">Negative</th>
                <th className="py-1 pr-3 text-right">Urgency</th>
                <th className="py-1 pr-3">Mostly</th>
                <th className="py-1">Top topic</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {customers.map((c) => (
                <tr key={c.customerId} className="hover:bg-gray-50">
                  <td className="py-1.5 pr-3">
                    <button
                      type="button"
                      onClick={() => onSelectCustomer(c.customerId)}
                      className="max-w-[14rem] truncate text-left font-medium text-slate-900 hover:underline"
                      title={c.customerName}
                    >
                      {c.customerName ?? c.customerId}
                    </button>
                  </td>
                  <td className="py-1.5 pr-3 text-right font-semibold tabular-nums">
                    {c.count}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{c.active}</td>
                  <td
                    className={clsx(
                      "py-1.5 pr-3 text-right tabular-nums",
                      c.negativeCount > 0 && "text-rose-700",
                    )}
                  >
                    {c.negativeCount}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">
                    {c.avgUrgency.toFixed(1)}
                  </td>
                  <td className="py-1.5 pr-3">
                    <CategoryPill category={c.dominantCategory} />
                  </td>
                  <td className="py-1.5 text-gray-700">{c.topTopic}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export function KnowledgeGapsCard({
  gaps,
}: {
  gaps: ITicketInsightKnowledgeGap[];
}) {
  return (
    <Card
      title="Knowledge gaps"
      description="Questions that keep coming back. Each one is a help article, a tooltip, or a product tweak waiting to happen."
    >
      {gaps.length === 0 ? (
        <EmptyState>
          No question topic has come up more than once in this scope.
        </EmptyState>
      ) : (
        <ul className="divide-y divide-gray-100">
          {gaps.map((gap) => (
            <li key={gap.topic} className="py-2">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-slate-900">{gap.topic}</span>
                <span className="shrink-0 text-xs text-gray-600">
                  asked <span className="font-semibold">{gap.count}</span> times
                  by {gap.customerCount} customer{gap.customerCount === 1 ? "" : "s"}
                </span>
              </div>
              <ul className="mt-1 space-y-0.5">
                {gap.exampleSubjects.map((subject, i) => (
                  <li key={i} className="truncate text-xs text-gray-600" title={subject}>
                    “{subject}”
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function BacklogAgingCard({
  buckets,
}: {
  buckets: ITicketInsightAgeBucket[];
}) {
  const total = buckets.reduce((s, b) => s + b.count, 0);
  const max = Math.max(0, ...buckets.map((b) => b.count));

  return (
    <Card
      title="Backlog age"
      description={`${total} active ticket${total === 1 ? "" : "s"} by how long they have been open, split by AI category.`}
      action={
        <Link
          to="/tickets?quickFilter=Active"
          className="text-xs text-orange-700 hover:underline"
        >
          Open the list
        </Link>
      }
    >
      {total === 0 ? (
        <EmptyState>No active analysed tickets in this scope.</EmptyState>
      ) : (
        <div className="space-y-2">
          {buckets.map((bucket) => (
            <div key={bucket.label}>
              <BarRow
                label={bucket.label}
                value={bucket.count}
                max={max}
                barClassName={
                  bucket.minDays >= 14
                    ? "bg-rose-400"
                    : bucket.minDays >= 7
                      ? "bg-orange-400"
                      : "bg-sky-400"
                }
              />
              {bucket.count > 0 ? (
                <div className="ml-[9.75rem] mr-[5.75rem]">
                  <StackedBar
                    className="mt-0.5 h-1"
                    segments={Object.entries(bucket.byCategory).map(([name, value]) => {
                      const category = categoryByName[name];
                      return {
                        key: name,
                        value,
                        className:
                          category !== undefined
                            ? categoryBarClassMap[category]
                            : "bg-gray-300",
                        title: `${
                          category !== undefined ? categoryLabels[category] : name
                        }: ${value}`,
                      };
                    })}
                  />
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function TagsCard({ tags }: { tags: ITicketInsightTag[] }) {
  const max = Math.max(1, ...tags.map((t) => t.count));

  return (
    <Card
      title="Keywords"
      description="Tags the model attached. Size follows how often each appears."
    >
      {tags.length === 0 ? (
        <EmptyState>No tags yet.</EmptyState>
      ) : (
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          {tags.map((tag) => {
            const weight = tag.count / max;
            return (
              <span
                key={tag.tag}
                title={`${tag.count} ticket${tag.count === 1 ? "" : "s"}`}
                className={clsx(
                  "leading-tight",
                  weight > 0.66
                    ? "text-lg font-semibold text-slate-900"
                    : weight > 0.33
                      ? "text-base font-medium text-slate-800"
                      : "text-sm text-slate-600",
                )}
              >
                {tag.tag}
                <span className="ml-0.5 text-xs text-gray-400">{tag.count}</span>
              </span>
            );
          })}
        </div>
      )}
    </Card>
  );
}
